<template>
  <div class="edit-row edit-row--b-top edit-row--b-bottom">
    <div class="edit-row__label">{{ t('quickEditBar.visibilityFrom')}}</div>
    <field-select
        :label="t('sidebar.visibility.label.first').toLowerCase()"
        :label-inline="true"
        :label-classname="'mr--39'"
        :select-classname="'field--w-38'"
        :class="'mod--edit-bar mode--label-w-normal'"
        :options="zoomLevels"
        :options-available="zoomAvailableLevelsFirstLayer"
        v-model:selected="visibility.min"
    ></field-select>
    <field-select
        :label="t('sidebar.visibility.label.last').toLowerCase()"
        :label-inline="true"
        :label-classname="'mr--39'"
        :select-classname="'field--w-38'"
        :class="'mod--edit-bar mode--label-w-normal'"
        :options="zoomLevels"
        :options-available="zoomAvailableLevelsLastLayer"
        v-model:selected="visibility.max"
    ></field-select>
  </div>
  <div class="edit-row">
    <div class="edit-row__label mr--25">Visibility:</div>
    <div class="section-row section-row--triple-max mod--edit-bar flex--grow">
      <div class="row-item">
        <field-toggle
            :label="nodeHiddenLabel"
            v-model:checked="visibility.hidden"
            class="mod--edit-bar"
        ></field-toggle>
      </div>
    </div>
  </div>
	<popup-confirm
		:popup-status="popupActive"
		:content="t('popup.confirm.deleteEdgesNotOverlappedMessage')"
		:confirm-button="t('popup.confirm.actions.deleteRelatedEdges')"
		:cancel-button="t('popup.confirm.actions.cancelDeleteRelatedEdges')"
		@action-confirm="confirmEdgesRemove"
		@action-cancel="nodeVisibilityReset"
		:loading="loading"
	></popup-confirm>
</template>

<script>
import FieldSelect from '@/components/fields/FieldSelect';
import FieldToggle from '@/components/fields/FieldToggle.vue';
import PopupConfirm from '@/components/popup/PopupConfirm';
import {ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useStore} from 'vuex';
import MapUtils from '@/services/canvas/map-area/MapUtils';
import {computed, reactive} from 'vue';
import useLoading from "@/utils/Loading";
import usePopup from "@/utils/Popup";

export default {
  components: {
    FieldSelect,
    FieldToggle,
	  PopupConfirm
  },
  setup() {
    const {t} = useI18n();
    const store = useStore();
		// loading
	  const {loading, loadingShow, loadingHide} = useLoading();
	  // popup
	  const { popupActive, popupOpen, popupClose } = usePopup();
    const { getZoomLevelOptions } = MapUtils();
    const currentNode = computed(() => store.getters['nodes/getSelectedNode']);
    function nodeVisibilityUpdate(nodeData){
      store.dispatch('nodes/visibility/nodeVisibilityUpdate', nodeData);
    }
    async function nodeVisibilitySave(nodeData){
      await store.dispatch('nodes/visibility/nodeVisibilitySave', nodeData);
    }
    function nodeHiddenToggle(status){
      const nodeId = currentNode.value.id;
      store.dispatch('nodes/hidden/nodeUpdateHidden', {[nodeId]: status});
      store.dispatch('nodes/hidden/nodeSaveHidden', {[nodeId]: status});
    }

    const layersAmount = computed(() => store.getters["layers/getLayersAmount"]);
    const zoomLevels = computed(() => getZoomLevelOptions(layersAmount.value));
		const edges = computed(() => store.getters["edges/getEdges"]);
		const edgesRelated = computed(() => {
			const edgesFrom = edges.value.filter(edge => edge.fromNodeId === currentNode.value.id);
			const edgesTo = edges.value.filter(edge => edge.toNodeId === currentNode.value.id);
			return {
				edgesFrom: edgesFrom.map(edge => edge.id),
				edgesTo: edgesTo.map(edge => edge.id),
				edges: [...edgesFrom, ...edgesTo]
			}
			})
	  // temporary data if edges don't overlap and have to be removed
	  const nodeVisibilityTmp = ref(null);
	  const edgesRelatedToRemove = ref(null);
	  
		function getEdgesRelatedLevelsOverlapped(min, max){
			const connectedEdges = edgesRelated.value.edges;
			const levelsOverlapped = [];
			
			connectedEdges.forEach(edge => {
				const edgeVisibilityRange = getZoomLevelOptions(edge.visibilityMax, edge.visibilityMin); // [1, 2, 3, 4...]
				const nodeVisibilityRange = getZoomLevelOptions(max, min);
				const result = {id: edge.id, levelsOverlapped: []}
				
				nodeVisibilityRange.forEach(level => {
					if(edgeVisibilityRange.indexOf(level) > -1){
						result.levelsOverlapped.push(level);
					}
				})
				levelsOverlapped.push(result);
			})
			return levelsOverlapped;
		}
		function getEdgesIdsToRemove(min, max){
			return getEdgesRelatedLevelsOverlapped(min, max)
				.filter(edge => !edge.levelsOverlapped.length).map(edge => edge.id);
		}
		async function confirmEdgesRemove(){
			loadingShow();
			
			try{
				// remove edges which don't overlap
				edgesRemove(edgesRelatedToRemove.value);
				await edgesRemoveRequest(edgesRelatedToRemove.value);
				// update node visibility
				nodeVisibilityUpdate(nodeVisibilityTmp.value);
				await nodeVisibilitySave(nodeVisibilityTmp.value);
			} catch(error){
				console.log(error);
			}
			
			nodeVisibilityTmp.value = null;
			edgesRelatedToRemove.value = null;
			loadingHide();
			popupClose();
			
		}
		function nodeVisibilityReset(){
			nodeVisibilityTmp.value = null;
			edgesRelatedToRemove.value = null;
			popupClose();
		}
		function edgesRemove(edgesIds){
			store.dispatch('edges/remove/edgesRemove', edgesIds);
		}
		async function edgesRemoveRequest(edgesIds){
			await store.dispatch('edges/remove/edgesRemoveRequest', edgesIds);
		}
		
		
    // calc available levels of first layer(visibilityMin) depends on value of node visibilityMax value
    const zoomAvailableLevelsFirstLayer = computed(()=>
        getZoomLevelOptions(Math.min(layersAmount.value, currentNode.value.visibilityMax), 1)
    );
    // calc available levels of last layer(visibilityMax) depends on value of node visibilityMin value
    const zoomAvailableLevelsLastLayer = computed(()=>
        getZoomLevelOptions(layersAmount.value, currentNode.value.visibilityMin)
    );
		
    const nodeVisibilityMin = computed({
      get(){return currentNode.value?.visibilityMin},
      set(value){
	      const nodeData = {
		      id: currentNode.value.id,
		      visibility: {
			      visibilityMin: value,
			      visibilityMax: currentNode.value.visibilityMax
		      }
	      };
				const edgesRelatedLevelsOverlapped = getEdgesIdsToRemove(value, currentNode.value.visibilityMax);
				if(edgesRelatedLevelsOverlapped.length){
					nodeVisibilityTmp.value = nodeData;
					edgesRelatedToRemove.value = edgesRelatedLevelsOverlapped;
					popupOpen();
					return;
				}
				
        nodeVisibilityUpdate(nodeData);
        nodeVisibilitySave(nodeData);
      }
    })
    const nodeVisibilityMax = computed({
      get(){return currentNode.value?.visibilityMax},
      set(value){
	      const nodeData = {
		      id: currentNode.value.id,
		      visibility: {
			      visibilityMin: currentNode.value.visibilityMin,
			      visibilityMax: value
		      }
	      };
	      const edgesRelatedLevelsOverlapped = getEdgesIdsToRemove(currentNode.value.visibilityMin, value);
	      if(edgesRelatedLevelsOverlapped.length){
		      nodeVisibilityTmp.value = nodeData;
		      edgesRelatedToRemove.value = edgesRelatedLevelsOverlapped;
		      popupOpen();
		      return;
	      }
				
        nodeVisibilityUpdate(nodeData)
        nodeVisibilitySave(nodeData);
      }
    })

    const nodeHidden = computed({
      get(){return !currentNode.value?.hidden},
      set(value){
        nodeHiddenToggle(!value);
      }
    })
    const nodeHiddenLabel = computed(() => currentNode.value?.hidden ? 'off' : 'on');

    const visibility = reactive({
      min: nodeVisibilityMin,
      max: nodeVisibilityMax,
      hidden: nodeHidden
    });

    return{
      t,
	    loading,
	    popupActive,
      currentNode,
      nodeHiddenLabel,
      visibility,
	    confirmEdgesRemove,
	    nodeVisibilityReset,
      zoomAvailableLevelsFirstLayer,
      zoomAvailableLevelsLastLayer,
      zoomLevels
    }
  }
}
</script>

<style lang="scss">

</style>
